import { Edit, KeyboardArrowDown, KeyboardArrowRight, UploadFile } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { IPublication } from '@opengov/stories-shared/types';
import React, { useEffect, useState } from 'react';
import { useDebounce } from 'react-use';
import { getFragmentData, graphql } from '../../../../gql-generated/hasura';
import { getCompletedLocaleText } from '../../../../locale/utils';
import { PublicationViewLoadingSkeleton } from '../PublicationView/PublicationView/PublicationViewLoadingSkeleton';
import locale from './customReports-locale.json';
import { CustomReportsEmptyState } from './CustomReportsEmptyState';
import { styles } from './CustomReportsTable.styles';
import { isProcessingCompleteOrFailed } from './CustomReportsTable.utils';
import { CustomReportsTableExpandedRow } from './CustomReportsTableExpandedRow';
import { CustomReportsTableStatusCell } from './CustomReportsTableStatusCell';
import { EditReportModal } from './EditReportModal/EditReportModal';
import { SortOption, useFetchCustomReports } from './hooks/useFetchCustomReports';
import { useFetchCustomReportStoryCounts } from './hooks/useFetchCustomReportStoryCounts';
import { NewExcelFileModal } from './NewExcelFileModal/NewExcelFileModal';
import { TableCellSpinner } from './TableCellSpinner';

const CustomReportsTable_CustomReportFragment = graphql(`
  fragment CustomReportsTable_CustomReportFragment on spreadsheets {
    id
    name
    processingStatus
    spreadsheetNamedRanges_aggregate {
      aggregate {
        count
      }
    }
    ...CustomReportsTableStatusCell_CustomReportFragment
    ...CustomReportsTableExpandedRow_CustomReportFragment
  }
`);

/**
 * Fragment for stories count is separated from primary
 * query for performance reasons. This one does not block
 * the main table from rendering.
 */

const CustomReportsTable_StoryCountFragment = graphql(`
  fragment CustomReportsTable_StoryCountFragment on spreadsheetStoryCounts {
    spreadsheetId
    countOfUniqueStories
  }
`);

export const ROWS_PER_PAGE = 10;
const DEBOUNCE_TIME_MS = 300;

type Props = {
  publication: IPublication;
  onUploadClick: () => void;
};

export function CustomReportsTable({ publication, onUploadClick }: Props) {
  const [page, setPage] = useState(0);
  const [sortBy, setSortBy] = useState<SortOption>(SortOption.UpdatedMostRecent);
  const [expandedRows, setExpandedRows] = useState<{ [key: string]: boolean }>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [editingReport, setEditingReport] = useState<{ id: string; name: string } | null>(null);
  const [updatingSpreadsheet, setUpdatingSpreadsheet] = useState<{ id: string; name: string } | null>(null);
  useDebounce(() => setDebouncedSearchQuery(searchQuery), DEBOUNCE_TIME_MS, [searchQuery]);

  const { data: customReportsData, isLoading: isLoadingCustomReports } = useFetchCustomReports({
    publicationId: publication.id,
    searchQuery: debouncedSearchQuery,
    sortBy,
    limit: ROWS_PER_PAGE,
    offset: page * ROWS_PER_PAGE
  });

  const customReportsFragment = customReportsData?.spreadsheets ?? [];
  const customReportsTotalCount = customReportsData?.totalCount ?? 0;
  const customReports = getFragmentData(CustomReportsTable_CustomReportFragment, customReportsFragment) ?? [];

  // Fetched separately to avoid blocking the main table from rendering
  const { data: storyCountsFragment, isLoading: isLoadingStoryCount } = useFetchCustomReportStoryCounts({
    spreadsheetIds: customReports.map((report) => report.id)
  });
  const storyCountsData = getFragmentData(CustomReportsTable_StoryCountFragment, storyCountsFragment) ?? [];

  // Reset to page 0 when search or sort changes
  useEffect(() => {
    setPage(0);
  }, [debouncedSearchQuery, sortBy]);

  const isRowExpanded = (rowId: string) => expandedRows[rowId] ?? false;

  const hasNoResults = !isLoadingCustomReports && customReports.length === 0;
  const hasActiveSearch = debouncedSearchQuery.trim().length > 0;
  const shouldShowEmptyState = hasNoResults && !hasActiveSearch;

  if (shouldShowEmptyState) {
    return <CustomReportsEmptyState onUploadButtonClick={onUploadClick} />;
  }

  return (
    <>
      <Box sx={styles.filtersContainer}>
        <TextField
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          label={locale.table.searchLabel}
          sx={[styles.textField, styles.searchTextField]}
          inputProps={{ className: 'no-format' }}
        />

        <TextField
          value={sortBy}
          label={locale.table.sortLabel}
          select
          sx={[styles.textField, styles.sortTextField]}
          inputProps={{ className: 'no-format' }}
          onChange={(e) => setSortBy(e.target.value as SortOption)}
        >
          <MenuItem value={SortOption.NameAsc}>{locale.table.sortNameAsc}</MenuItem>
          <MenuItem value={SortOption.NameDesc}>{locale.table.sortNameDesc}</MenuItem>
          <MenuItem value={SortOption.CreatedMostRecent}>{locale.table.sortCreatedMostRecent}</MenuItem>
          <MenuItem value={SortOption.CreatedLeastRecent}>{locale.table.sortCreatedLeastRecent}</MenuItem>
          <MenuItem value={SortOption.UpdatedMostRecent}>{locale.table.sortUpdatedMostRecent}</MenuItem>
          <MenuItem value={SortOption.UpdatedLeastRecent}>{locale.table.sortUpdatedLeastRecent}</MenuItem>
        </TextField>
      </Box>

      {isLoadingCustomReports ? (
        <PublicationViewLoadingSkeleton />
      ) : hasNoResults ? (
        <Box sx={styles.tableContainer} data-test="custom-reports-table-no-results">
          <Typography variant="body2" sx={styles.noResultsText}>
            {locale.table.noResultsFound}
          </Typography>
        </Box>
      ) : (
        <TableContainer sx={styles.tableContainer} data-test="custom-reports-table">
          <Table>
            <TableBody>
              {customReports.map((report) => {
                const numNamedRanges = report.spreadsheetNamedRanges_aggregate.aggregate?.count ?? 0;

                const numStories =
                  storyCountsData.find((count) => count.spreadsheetId === report.id)?.countOfUniqueStories ?? 0;

                return (
                  <React.Fragment key={report.id}>
                    <TableRow sx={styles.tableRow}>
                      <TableCell sx={styles.arrowCell}>
                        <IconButton
                          data-test="custom-reports-table-arrow-button"
                          size="small"
                          onClick={() => {
                            setExpandedRows((prev) => ({
                              ...prev,
                              [report.id]: !isRowExpanded(report.id)
                            }));
                          }}
                        >
                          {isRowExpanded(report.id) ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                        </IconButton>
                      </TableCell>
                      <TableCell sx={styles.reportNameCell}>
                        <Box>
                          <Typography variant="body2" sx={styles.reportNameText}>
                            {report.name}
                          </Typography>
                          <Typography variant="caption">
                            {numNamedRanges === 1
                              ? locale.table.oneNamedRange
                              : getCompletedLocaleText(locale.table.namedRanges, {
                                  numNamedRanges: numNamedRanges
                                })}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={styles.storiesCountCell}>
                        {isLoadingStoryCount ? (
                          <Box sx={styles.spinnerContainer}>
                            <TableCellSpinner />
                          </Box>
                        ) : (
                          <Box component="span">
                            {numStories === 1
                              ? locale.table.usedInOneStory
                              : getCompletedLocaleText(locale.table.usedInStories, { numStories })}
                          </Box>
                        )}
                      </TableCell>
                      <CustomReportsTableStatusCell customReport={report} />
                      <TableCell align="right" sx={styles.actionsCell}>
                        <Button
                          startIcon={<Edit />}
                          sx={styles.editButton}
                          data-test="custom-reports-table-edit-button"
                          onClick={() => setEditingReport({ id: report.id, name: report.name })}
                          disabled={!isProcessingCompleteOrFailed(report.processingStatus)}
                        >
                          {locale.table.editButton}
                        </Button>
                        <Button
                          startIcon={<UploadFile />}
                          sx={styles.updateButton}
                          data-test="custom-reports-table-update-button"
                          onClick={() => setUpdatingSpreadsheet({ id: report.id, name: report.name })}
                          disabled={!isProcessingCompleteOrFailed(report.processingStatus)}
                        >
                          {locale.table.updateFileButton}
                        </Button>
                      </TableCell>
                    </TableRow>

                    {isRowExpanded(report.id) ? (
                      <CustomReportsTableExpandedRow key={report.id} spreadsheetId={report.id} customReport={report} />
                    ) : null}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={customReportsTotalCount}
            page={page}
            onPageChange={(_, newPage) => {
              setPage(newPage);
            }}
            rowsPerPage={ROWS_PER_PAGE}
            rowsPerPageOptions={[]}
            labelDisplayedRows={({ from, to, count }) =>
              getCompletedLocaleText(locale.table.paginationLabel, { from, to, count })
            }
          />
        </TableContainer>
      )}

      {editingReport ? (
        <EditReportModal
          spreadsheetId={editingReport.id}
          publicationId={publication.id}
          initialName={editingReport.name}
          onClose={() => setEditingReport(null)}
        />
      ) : null}

      {updatingSpreadsheet ? (
        <NewExcelFileModal
          publication={publication}
          mode={{
            variant: 'update',
            spreadsheetId: updatingSpreadsheet.id,
            initialReportName: updatingSpreadsheet.name
          }}
          onClose={() => setUpdatingSpreadsheet(null)}
        />
      ) : null}
    </>
  );
}
